import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CropModal from "@/components/CropModal";
import VideoPlayer from "@/components/VideoPlayer";
import { resolveStream } from "@/lib/stream";
import { pushSupported, pushStatus, subscribePush, unsubscribePush } from "@/lib/push";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import {
  Eye, Heart, Star, Bell, MessageCircle, Share2, DollarSign, Wallet, Radio, Loader2,
  LayoutDashboard, Signal, WifiOff, TrendingUp, ArrowDownToLine, CalendarDays, Plus, Trash2,
  Clock, CheckCircle2, XCircle, PlusCircle, Tv, Upload, Image as ImageIcon, Circle, PlayCircle, BellRing,
  Smartphone, Monitor, Tablet, Globe, MapPin, BarChart3, Timer, FileText, BadgeCheck,
  Facebook, Youtube, Instagram, Twitter, Send, Music2, Link2,
  Pin, PinOff, EyeOff, Reply, Megaphone, MessagesSquare, ExternalLink,
} from "lucide-react";

const SOCIALS = [
  { key: "facebook", label: "Facebook", icon: Facebook, color: "text-[#1877F2]", ph: "https://facebook.com/votrechaine" },
  { key: "youtube", label: "YouTube", icon: Youtube, color: "text-[#FF0000]", ph: "https://youtube.com/@votrechaine" },
  { key: "instagram", label: "Instagram", icon: Instagram, color: "text-[#E4405F]", ph: "https://instagram.com/votrechaine" },
  { key: "tiktok", label: "TikTok", icon: Music2, color: "text-foreground", ph: "https://tiktok.com/@votrechaine" },
  { key: "x", label: "X (Twitter)", icon: Twitter, color: "text-foreground", ph: "https://x.com/votrechaine" },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "text-[#25D366]", ph: "https://wa.me/243… ou numéro" },
  { key: "telegram", label: "Telegram", icon: Send, color: "text-[#229ED9]", ph: "https://t.me/votrechaine" },
];

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${color}`}><Icon size={18} /></div>
      <p className="font-heading font-extrabold text-2xl">{(value || 0).toLocaleString()}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return `il y a ${Math.floor(s / 86400)} j`;
}

function fmtDuration(sec) {
  sec = sec || 0;
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  if (h) return `${h}h ${m}min`;
  if (m) return `${m}min ${s}s`;
  return `${s}s`;
}

const DEVICE_META = {
  mobile: { icon: Smartphone, label: "Mobile", color: "text-rdcblue" },
  desktop: { icon: Monitor, label: "Ordinateur", color: "text-emerald-600" },
  tablet: { icon: Tablet, label: "Tablette", color: "text-rdcgold" },
  tv: { icon: Tv, label: "Smart TV", color: "text-rdcred" },
};

function countryFlag(code) {
  if (!code || code.length !== 2) return "🌐";
  return code.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

function BarRow({ label, icon: Icon, iconColor, count, total, testid }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      {Icon && <Icon size={16} className={`${iconColor} shrink-0`} />}
      <span className="text-sm w-28 shrink-0 truncate">{label}</span>
      <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} /></div>
      <span className="text-xs font-semibold text-muted-foreground w-14 text-right">{count} · {pct}%</span>
    </div>
  );
}

function StreamStatusBadge({ status, checkedAt }) {
  const map = {
    online: { cls: "bg-emerald-500/15 text-emerald-600", icon: CheckCircle2, label: "Flux en ligne" },
    offline: { cls: "bg-rdcred/15 text-rdcred", icon: XCircle, label: "Flux hors ligne" },
  };
  const c = map[status] || { cls: "bg-slate-500/15 text-slate-500", icon: Circle, label: "Non vérifié" };
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${c.cls}`} data-testid="mgr-stream-status">
      <Icon size={13} /> {c.label}
      {checkedAt && <span className="font-normal opacity-70">· {timeAgo(checkedAt)}</span>}
    </span>
  );
}

export default function ManagerDashboard() {
  const { user } = useAuth();
  const [myMedia, setMyMedia] = useState(null);
  const [mediaList, setMediaList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dash, setDash] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [profile, setProfile] = useState({});
  const [stream, setStream] = useState({});
  const [wd, setWd] = useState({ amount: "", method: "mpesa", destination: "" });
  const [schedule, setSchedule] = useState([]);
  const [prog, setProg] = useState({ day_of_week: "1", start_time: "08:00", end_time: "10:00", title: "", host: "" });
  const [uploading, setUploading] = useState("");
  const [cropState, setCropState] = useState(null); // { file, kind }
  const [supports, setSupports] = useState([]);
  const [showTest, setShowTest] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [streamHistory, setStreamHistory] = useState([]);
  const [weeklyReport, setWeeklyReport] = useState(true);
  const [cert, setCert] = useState(null);
  const [certDocs, setCertDocs] = useState({ enterprise: "", authorization: "", id_card: "" });
  const [certExp, setCertExp] = useState({ enterprise: "", authorization: "", id_card: "" });
  const [certUploading, setCertUploading] = useState("");
  const [certRequesting, setCertRequesting] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushDismissed, setPushDismissed] = useState(() => localStorage.getItem("mgr_push_dismissed") === "1");
  const [checkingStream, setCheckingStream] = useState(false);
  const [mgrComments, setMgrComments] = useState([]);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [cmtBusy, setCmtBusy] = useState("");
  const [broadcast, setBroadcast] = useState({ title: "", message: "" });
  const [broadcasting, setBroadcasting] = useState(false);

  const DAYS = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];

  const isManager = !!user && (user.role === "manager" || user.role === "admin");

  useEffect(() => {
    api.get("/me/my-media").then((r) => setMyMedia(r.data)).catch(() => setMyMedia([]));
    if (isManager) {
      api.get("/manager/media").then((r) => { setMediaList(r.data); if (r.data[0]) setSelected(r.data[0].id); });
    }
  }, [isManager]);

  const load = useCallback(async (id) => {
    const { data } = await api.get(`/manager/media/${id}/dashboard`);
    setDash(data);
    setProfile({ description: data.media.description, website: data.media.website, address: data.media.address, contacts_phone: data.media.contacts?.phone || "", contacts_email: data.media.contacts?.email || "", socials: data.media.socials || {} });
    setStream({ hls_url: data.media.hls_url || "", audio_url: data.media.audio_url || "", is_live: data.media.is_live });
    const w = await api.get(`/manager/media/${id}/wallet`);
    setWalletData(w.data);
    api.get(`/manager/media/${id}/supports`).then((r) => setSupports(r.data)).catch(() => setSupports([]));
    api.get(`/manager/media/${id}/analytics`).then((r) => setAnalytics(r.data)).catch(() => setAnalytics(null));
    api.get(`/manager/media/${id}/stream/history`).then((r) => setStreamHistory(r.data)).catch(() => setStreamHistory([]));
    api.get(`/manager/media/${id}/certification`).then((r) => { setCert(r.data); setCertDocs({ enterprise: "", authorization: "", id_card: "" }); }).catch(() => setCert(null));
    api.get(`/manager/media/${id}/comments`).then((r) => setMgrComments(r.data)).catch(() => setMgrComments([]));
    const sc = await api.get(`/manager/media/${id}/schedule`);
    setSchedule(sc.data);
  }, []);

  useEffect(() => { if (selected) load(selected); }, [selected, load]);

  useEffect(() => { if (isManager && pushSupported()) pushStatus().then(setPushOn); }, [isManager]);

  useEffect(() => { api.get("/me/weekly-report").then((r) => setWeeklyReport(r.data.enabled)).catch(() => {}); }, []);

  const toggleWeekly = async () => {
    const next = !weeklyReport;
    setWeeklyReport(next);
    try { await api.put("/me/weekly-report", { enabled: next }); toast.success(next ? "Rapport hebdo activé" : "Rapport hebdo désactivé"); }
    catch (e) { setWeeklyReport(!next); toast.error(apiErr(e)); }
  };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushOn) { await unsubscribePush(); setPushOn(false); toast.success("Notifications push désactivées"); }
      else { await subscribePush(); setPushOn(true); toast.success("Notifications push activées ! Vous serez alerté à chaque nouveau soutien."); }
    } catch (e) { toast.error(e.message || "Impossible d'activer les notifications"); }
    finally { setPushBusy(false); }
  };
  const dismissPush = () => { setPushDismissed(true); localStorage.setItem("mgr_push_dismissed", "1"); };

  const uploadCertDoc = async (kind, fileObj) => {
    if (!selected || !fileObj) return;
    setCertUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const { data } = await api.post(`/manager/media/${selected}/certification/upload?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCertDocs((p) => ({ ...p, [kind]: data.url }));
      toast.success("Document ajouté");
    } catch (e) { toast.error(apiErr(e)); } finally { setCertUploading(""); }
  };

  const requestCert = async () => {
    if (!certDocs.enterprise || !certDocs.authorization || !certDocs.id_card) {
      toast.error("Veuillez fournir les 3 documents requis."); return;
    }
    setCertRequesting(true);
    try {
      await api.post(`/manager/media/${selected}/certification/request`, {
        enterprise_doc: certDocs.enterprise, authorization_doc: certDocs.authorization, id_card_doc: certDocs.id_card,
        enterprise_expires: certExp.enterprise || null, authorization_expires: certExp.authorization || null, id_card_expires: certExp.id_card || null });
      toast.success("Demande de certification envoyée !");
      const r = await api.get(`/manager/media/${selected}/certification`); setCert(r.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setCertRequesting(false); }
  };

  const checkStreamNow = async () => {
    if (!selected) return;
    setCheckingStream(true);
    try {
      const { data } = await api.post(`/manager/media/${selected}/stream/check`);
      setDash((d) => d ? { ...d, media: { ...d.media, stream_status: data.stream_status, stream_checked_at: data.checked_at } } : d);
      if (data.stream_status === "online") toast.success("Flux en ligne ✅");
      else if (data.stream_status === "offline") toast.error("Flux hors ligne ⚠");
      else toast("Aucune URL de flux à vérifier");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setCheckingStream(false); }
  };

  const reloadComments = async () => {
    try { const { data } = await api.get(`/manager/media/${selected}/comments`); setMgrComments(data); } catch {}
  };
  const pinComment = async (c) => {
    setCmtBusy(c.id);
    try { const { data } = await api.post(`/manager/comments/${c.id}/pin`); toast.success(data.pinned ? "Commentaire épinglé" : "Épingle retirée"); await reloadComments(); }
    catch (e) { toast.error(apiErr(e)); } finally { setCmtBusy(""); }
  };
  const hideComment = async (c) => {
    setCmtBusy(c.id);
    try { const { data } = await api.post(`/manager/comments/${c.id}/hide`); toast.success(data.status === "hidden" ? "Commentaire masqué" : "Commentaire réaffiché"); await reloadComments(); }
    catch (e) { toast.error(apiErr(e)); } finally { setCmtBusy(""); }
  };
  const replyComment = async (c) => {
    if (!replyText.trim()) return;
    setCmtBusy(c.id);
    try { await api.post(`/manager/comments/${c.id}/reply`, { content: replyText.trim() }); toast.success("Réponse publiée"); setReplyFor(null); setReplyText(""); await reloadComments(); }
    catch (e) { toast.error(apiErr(e)); } finally { setCmtBusy(""); }
  };
  const sendBroadcast = async () => {
    setBroadcasting(true);
    try {
      const { data } = await api.post(`/manager/media/${selected}/broadcast`, { title: broadcast.title || null, message: broadcast.message || null });
      toast.success(data.recipients > 0 ? `Alerte envoyée à ${data.recipients} abonné(s) !` : "Aucun abonné à alerter pour l'instant.");
      setBroadcast({ title: "", message: "" });
    } catch (e) { toast.error(apiErr(e)); } finally { setBroadcasting(false); }
  };

  if (myMedia === null) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-primary" size={32} /></div>;

  // Not yet managing an approved media -> show submission status / pending view
  if (!(isManager && mediaList.length > 0)) {
    const STATUS_UI = {
      pending: { icon: Clock, color: "text-rdcgold", bg: "bg-rdcgold/10", label: "En attente de validation", note: "Votre demande est en cours d'examen par l'administration. Vous pourrez gérer votre média dès qu'il sera validé." },
      approved: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-500/10", label: "Validé", note: "Votre média est validé. Rechargez la page pour accéder à la gestion." },
      verified: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-500/10", label: "Vérifié", note: "Votre média est vérifié. Rechargez la page pour accéder à la gestion." },
      rejected: { icon: XCircle, color: "text-rdcred", bg: "bg-rdcred/10", label: "Refusé", note: "Votre demande n'a pas été retenue. Contactez l'administration pour plus d'informations." },
      suspended: { icon: XCircle, color: "text-rdcred", bg: "bg-rdcred/10", label: "Suspendu", note: "Ce média est actuellement suspendu. Contactez l'administration." },
    };
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 mt-6" data-testid="media-status-view">
        <div className="flex items-center gap-2 mb-1"><LayoutDashboard className="text-rdcgold" /><h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Espace Média</h1></div>
        {myMedia.length === 0 ? (
          <div className="text-center py-16">
            <PlusCircle className="mx-auto text-muted-foreground mb-3" size={44} />
            <h2 className="font-heading font-bold text-lg">Aucun média inscrit</h2>
            <p className="text-muted-foreground mt-2 text-sm">Vous n'avez pas encore inscrit de média sur le portail.</p>
            <Button asChild className="mt-5 font-heading font-bold"><Link to="/register-media" data-testid="status-register-btn">Inscrire mon média</Link></Button>
          </div>
        ) : (
          <div className="space-y-3 mt-5">
            <p className="text-sm text-muted-foreground mb-2">Statut de vos demandes d'inscription :</p>
            {myMedia.map((m) => {
              const ui = STATUS_UI[m.status] || STATUS_UI.pending;
              const Icon = ui.icon;
              return (
                <div key={m.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`status-card-${m.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-11 h-11 rounded-xl bg-white border border-border flex items-center justify-center shrink-0">
                      {m.logo ? <img src={m.logo} alt="" className="w-full h-full rounded-xl object-cover" /> : (m.type === "tv" ? <Tv className="text-rdcblue" size={20} /> : <Radio className="text-rdcgold" size={20} />)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-heading font-bold truncate">{m.name}</p>
                      <p className="text-xs text-muted-foreground">{m.type === "tv" ? "Télévision" : "Radio"} · {m.province}</p>
                    </div>
                    <span className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${ui.bg} ${ui.color}`} data-testid={`status-badge-${m.status}`}>
                      <Icon size={13} /> {ui.label}
                    </span>
                  </div>
                  <p className="text-[13px] text-muted-foreground mt-3">{ui.note}</p>
                </div>
              );
            })}
            <Button asChild variant="outline" className="mt-2"><Link to="/register-media" data-testid="status-register-another"><PlusCircle size={16} className="mr-1.5" /> Inscrire un autre média</Link></Button>
          </div>
        )}
      </div>
    );
  }

  if (!dash) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-primary" size={32} /></div>;

  const s = dash.stats;
  const w = walletData?.wallet || {};

  const saveProfile = async () => {
    try {
      const socials = Object.fromEntries(Object.entries(profile.socials || {}).map(([k, v]) => [k, (v || "").trim()]).filter(([, v]) => v));
      await api.put(`/manager/media/${selected}`, { description: profile.description, website: profile.website, address: profile.address, contacts: { phone: profile.contacts_phone, email: profile.contacts_email }, socials });
      toast.success("Profil mis à jour");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveStream = async () => {
    try { await api.put(`/manager/media/${selected}/stream`, stream); toast.success("Flux mis à jour"); load(selected); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const uploadAsset = async (kind, fileObj) => {
    if (!fileObj) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      await api.post(`/manager/media/${selected}/upload?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(kind === "logo" ? "Logo mis à jour" : "Bannière mise à jour");
      load(selected);
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(""); }
  };
  const requestWithdrawal = async () => {
    try { await api.post(`/manager/media/${selected}/withdrawals`, { amount: parseFloat(wd.amount), method: wd.method, destination: wd.destination }); toast.success("Demande de retrait envoyée"); setWd({ amount: "", method: "mpesa", destination: "" }); load(selected); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addProg = async () => {
    if (!prog.title.trim()) { toast.error("Titre requis"); return; }
    try {
      await api.post(`/manager/media/${selected}/schedule`, { ...prog, day_of_week: parseInt(prog.day_of_week) });
      toast.success("Émission ajoutée");
      setProg({ ...prog, title: "", host: "" });
      const sc = await api.get(`/manager/media/${selected}/schedule`); setSchedule(sc.data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delProg = async (id) => {
    try { await api.delete(`/manager/schedule/${id}`); setSchedule((xs) => xs.filter((x) => x.id !== id)); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2"><LayoutDashboard className="text-rdcgold" /><h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Espace Média</h1></div>
        <div className="flex items-center gap-2">
          {mediaList.length > 1 && (
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger className="w-44 sm:w-56" data-testid="manager-media-select"><SelectValue /></SelectTrigger>
              <SelectContent>{mediaList.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button asChild variant="outline" size="sm" className="font-semibold"><Link to="/register-media" data-testid="mgr-add-media"><PlusCircle size={15} className="mr-1.5" /> <span className="hidden sm:inline">Inscrire un autre média</span><span className="sm:hidden">Ajouter</span></Link></Button>
        </div>
      </div>

      <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 mb-6">
        <img src={dash.media.logo} alt="" className="w-12 h-12 rounded-xl bg-white" />
        <div className="flex-1"><p className="font-heading font-bold">{dash.media.name}</p><p className="text-xs text-muted-foreground">{dash.media.province}</p></div>
        <div className="flex flex-col items-end gap-1.5">
          <span className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${s.is_live ? "bg-emerald-500/15 text-emerald-600" : "bg-slate-500/15 text-slate-500"}`}>
            {s.is_live ? <><Signal size={13} /> En direct</> : <><WifiOff size={13} /> Hors ligne</>}
          </span>
          <StreamStatusBadge status={dash.media.stream_status} checkedAt={dash.media.stream_checked_at} />
        </div>
      </div>

      {pushSupported() && (pushOn || !pushDismissed) && (
        <div className={`flex items-center gap-3 rounded-2xl border p-4 mb-6 ${pushOn ? "border-emerald-500/30 bg-emerald-500/5" : "border-rdcgold/40 bg-rdcgold/5"}`} data-testid="mgr-push-banner">
          <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${pushOn ? "bg-emerald-500/15 text-emerald-600" : "bg-rdcgold/15 text-rdcgold"}`}><BellRing size={20} /></span>
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-sm">{pushOn ? "Notifications push activées" : "Activez les notifications push"}</p>
            <p className="text-xs text-muted-foreground">{pushOn ? "Vous êtes alerté sur cet appareil à chaque nouveau soutien reçu, même app fermée." : "Recevez une alerte sur votre téléphone/navigateur dès qu'un soutien est reçu, même quand l'app est fermée."}</p>
          </div>
          {pushOn ? (
            <Button onClick={togglePush} disabled={pushBusy} variant="outline" size="sm" className="shrink-0 font-semibold" data-testid="mgr-push-toggle">
              {pushBusy ? <Loader2 className="animate-spin" size={15} /> : "Désactiver"}
            </Button>
          ) : (
            <div className="flex items-center gap-2 shrink-0">
              <Button onClick={dismissPush} variant="ghost" size="sm" className="font-semibold text-muted-foreground" data-testid="mgr-push-dismiss">Plus tard</Button>
              <Button onClick={togglePush} disabled={pushBusy} size="sm" className="font-semibold" data-testid="mgr-push-toggle">
                {pushBusy ? <Loader2 className="animate-spin" size={15} /> : "Activer"}
              </Button>
            </div>
          )}
        </div>
      )}

      <Tabs defaultValue="overview">
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="mgr-tab-overview">Aperçu</TabsTrigger>
          <TabsTrigger value="stream" data-testid="mgr-tab-stream">Streaming</TabsTrigger>
          <TabsTrigger value="audience" data-testid="mgr-tab-audience">Audience</TabsTrigger>
          <TabsTrigger value="wallet" data-testid="mgr-tab-wallet">Portefeuille</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="mgr-tab-schedule">Programmes</TabsTrigger>
          <TabsTrigger value="community" data-testid="mgr-tab-community">Communauté</TabsTrigger>
          <TabsTrigger value="profile" data-testid="mgr-tab-profile">Profil</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {(!dash.media.logo || !dash.media.banner || !(stream.hls_url || stream.audio_url)) && (
            <div className="rounded-2xl border-2 border-rdcgold/40 bg-rdcgold/5 p-5 mb-6" data-testid="onboarding-card">
              <h3 className="font-heading font-bold flex items-center gap-2 mb-1"><CheckCircle2 size={18} className="text-rdcgold" /> Complétez votre chaîne</h3>
              <p className="text-sm text-muted-foreground mb-3">Finalisez votre profil pour attirer plus d'audience.</p>
              <ul className="space-y-1.5 text-sm">
                {[
                  ["Ajouter un logo", !!dash.media.logo, "profile"],
                  ["Ajouter une bannière", !!dash.media.banner, "profile"],
                  [dash.media.type === "tv" ? "Configurer le flux TV (HLS)" : "Configurer le flux audio", !!(stream.hls_url || stream.audio_url), "stream"],
                ].map(([label, ok]) => (
                  <li key={label} className="flex items-center gap-2" data-testid={`onboarding-step-${ok ? "done" : "todo"}`}>
                    {ok ? <CheckCircle2 size={16} className="text-emerald-600 shrink-0" /> : <Circle size={16} className="text-muted-foreground shrink-0" />}
                    <span className={ok ? "line-through text-muted-foreground" : ""}>{label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <StatCard icon={Eye} label={dash.media.type === "tv" ? "Vues" : "Écoutes"} value={dash.media.type === "tv" ? s.views : s.listens} color="bg-rdcblue/10 text-rdcblue" />
            <StatCard icon={Signal} label="Audience live" value={s.live_viewers} color="bg-rdcred/10 text-rdcred" />
            <StatCard icon={Bell} label="Followers" value={s.followers} color="bg-primary/10 text-primary" />
            <StatCard icon={Heart} label="J'aime" value={s.likes} color="bg-rdcred/10 text-rdcred" />
            <StatCard icon={Star} label="Favoris" value={s.favorites} color="bg-rdcgold/10 text-rdcgold" />
            <StatCard icon={MessageCircle} label="Commentaires" value={s.comments} color="bg-primary/10 text-primary" />
            <StatCard icon={Share2} label="Partages" value={s.shares} color="bg-rdcblue/10 text-rdcblue" />
            <StatCard icon={DollarSign} label="Revenus nets ($)" value={w.total_net} color="bg-emerald-500/10 text-emerald-600" />
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 mb-6">
            <h3 className="font-heading font-bold mb-4 flex items-center gap-2"><TrendingUp size={18} className="text-primary" /> Audience (7 derniers jours)</h3>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={dash.daily_series}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#00A3E0" stopOpacity={0.4} /><stop offset="95%" stopColor="#00A3E0" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} width={30} />
                <Tooltip />
                <Area type="monotone" dataKey="views" stroke="#00A3E0" fill="url(#g)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="font-heading font-bold mb-3 flex items-center gap-2"><Heart size={18} className="text-rdcred" /> Soutiens récents</h3>
            {dash.recent_supports.length === 0 ? <p className="text-sm text-muted-foreground py-4">Aucun soutien pour l'instant.</p> :
              dash.recent_supports.map((sp) => (
                <div key={sp.id} className="flex items-center justify-between py-2 border-b border-border last:border-0 text-sm">
                  <span>{sp.user_name} · <span className="text-muted-foreground capitalize">{sp.method}</span></span>
                  <span className="font-heading font-bold text-emerald-600">+${sp.net}</span>
                </div>
              ))}
          </div>
        </TabsContent>

        <TabsContent value="stream">
          <div className="rounded-2xl border border-border bg-card p-5 space-y-4 max-w-xl">
            <div className="flex items-center justify-between">
              <div><p className="font-semibold text-sm">Statut de diffusion</p><p className="text-xs text-muted-foreground">Indiquez si vous êtes actuellement en direct</p></div>
              <button onClick={() => setStream((p) => ({ ...p, is_live: !p.is_live }))} data-testid="mgr-live-toggle"
                className={`rounded-full px-4 py-2 text-sm font-bold ${stream.is_live ? "bg-emerald-500 text-white" : "bg-secondary"}`}>{stream.is_live ? "EN DIRECT" : "HORS LIGNE"}</button>
            </div>
            {dash.media.type === "tv" ? (
              <div><label className="text-sm font-medium">URL HLS (.m3u8)</label><Input value={stream.hls_url} onChange={(e) => setStream((p) => ({ ...p, hls_url: e.target.value }))} placeholder="https://…/stream.m3u8" data-testid="mgr-hls-url" className="mt-1" /></div>
            ) : (
              <div><label className="text-sm font-medium">URL du flux audio</label><Input value={stream.audio_url} onChange={(e) => setStream((p) => ({ ...p, audio_url: e.target.value }))} placeholder="https://…/stream.mp3" data-testid="mgr-audio-url" className="mt-1" /></div>
            )}
            <p className="text-[11px] text-muted-foreground bg-secondary rounded-lg p-2">Ne renseignez jamais de clés ou secrets ici. Seule l'URL publique de diffusion est requise.</p>
            <div className="rounded-xl border border-rdcblue/30 bg-rdcblue/5 px-3.5 py-3" data-testid="mgr-ricast-hint">
              <p className="text-[13px] text-muted-foreground leading-relaxed mb-2.5">
                Vous n'avez pas encore d'URL de flux ? Créez votre diffusion en direct sur RiCast, puis collez l'URL obtenue ci-dessus.
              </p>
              <a href="https://rivivoo.com/cast" target="_blank" rel="noreferrer" data-testid="mgr-ricast-link"
                className="group inline-flex items-center gap-2.5 rounded-full border border-rdcred/40 bg-white px-4 py-2 font-heading font-bold text-sm text-rdcred shadow-sm transition-all hover:border-rdcred hover:shadow-md hover:-translate-y-0.5">
                <img src="/ricast-icon.png" alt="RiCast" className="w-6 h-6 rounded-md" />
                Créer mon flux sur RiCast
                <ExternalLink size={14} className="opacity-70 group-hover:opacity-100" />
              </a>
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap border-t border-border pt-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Disponibilité :</span>
                <StreamStatusBadge status={dash.media.stream_status} checkedAt={dash.media.stream_checked_at} />
              </div>
              <Button type="button" variant="outline" size="sm" onClick={checkStreamNow} disabled={checkingStream} data-testid="mgr-check-stream">
                {checkingStream ? <Loader2 className="animate-spin mr-1" size={15} /> : <Signal size={15} className="mr-1" />} Vérifier maintenant
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">Vos flux sont vérifiés automatiquement toutes les 5 minutes. Vous recevez une alerte (cloche + push) dès qu'une chaîne passe hors ligne ou revient en ligne.</p>
            {(() => {
              const streamUrl = (dash.media.type === "tv" ? stream.hls_url : stream.audio_url) || "";
              if (!streamUrl.trim()) return null;
              const LABELS = { hls: "Flux HLS en direct (.m3u8)", native: "Vidéo (fichier direct)", iframe: "Intégration / lecteur embarqué", none: "Non reconnu" };
              const kind = dash.media.type === "tv" ? resolveStream(streamUrl).kind : (streamUrl.match(/\.(mp3|aac|ogg|m3u8)(\?|$)/i) ? "native" : "iframe");
              return (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">Type détecté :</span>
                    <span data-testid="stream-detected-type" className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${kind === "hls" ? "bg-rdcblue/10 text-rdcblue" : kind === "iframe" ? "bg-rdcgold/15 text-amber-700" : kind === "native" ? "bg-emerald-500/10 text-emerald-600" : "bg-rdcred/10 text-rdcred"}`}>{LABELS[kind]}</span>
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowTest((v) => !v)} data-testid="mgr-test-stream"><PlayCircle size={15} className="mr-1" /> {showTest ? "Masquer le test" : "Tester le flux"}</Button>
                  </div>
                  {showTest && (
                    <div data-testid="stream-test-preview" className="mt-1">
                      {dash.media.type === "tv"
                        ? <VideoPlayer media={{ name: dash.media.name, hls_url: streamUrl }} />
                        : <audio controls autoPlay src={streamUrl} className="w-full" data-testid="stream-test-audio" />}
                    </div>
                  )}
                </div>
              );
            })()}
            <Button onClick={saveStream} className="font-semibold" data-testid="mgr-save-stream">Enregistrer le flux</Button>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 mt-6 max-w-xl" data-testid="mgr-stream-history">
            <h3 className="font-heading font-bold mb-1 flex items-center gap-2"><Clock size={18} className="text-primary" /> Journal du flux</h3>
            <p className="text-xs text-muted-foreground mb-3">Historique des passages en ligne / hors ligne détectés automatiquement.</p>
            {streamHistory.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">Aucun changement d'état enregistré pour l'instant.</p>
            ) : (
              <ul className="space-y-0">
                {streamHistory.map((h) => (
                  <li key={h.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0" data-testid={`stream-history-${h.status}`}>
                    {h.status === "online"
                      ? <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                      : <XCircle size={16} className="text-rdcred shrink-0" />}
                    <span className="text-sm font-medium">{h.status === "online" ? "Flux revenu en ligne" : "Flux passé hors ligne"}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{new Date(h.at).toLocaleString("fr-FR")}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="audience">
          {!analytics ? (
            <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">Chargement des statistiques…</div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="rounded-2xl border border-border bg-card p-4">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-2 bg-rdcblue/10 text-rdcblue"><Timer size={18} /></div>
                  <p className="font-heading font-extrabold text-2xl" data-testid="total-watch-time">{fmtDuration(analytics.total_watch_seconds)}</p>
                  <p className="text-xs text-muted-foreground">Temps total consommé</p>
                </div>
                <StatCard icon={BarChart3} label="Sessions de lecture" value={analytics.watch_sessions} color="bg-emerald-500/10 text-emerald-600" />
                <StatCard icon={Clock} label="Durée moyenne (s)" value={analytics.avg_session_seconds} color="bg-rdcgold/10 text-rdcgold" />
              </div>
              <p className="text-xs text-muted-foreground -mt-4">Statistiques des 30 derniers jours. Le temps consommé est mesuré en temps réel pendant la lecture.</p>

              <div className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-heading font-bold mb-4 flex items-center gap-2"><Smartphone size={18} className="text-primary" /> Par appareil</h3>
                {analytics.devices.length === 0 ? <p className="text-sm text-muted-foreground">Pas encore de données.</p> : (() => {
                  const total = analytics.devices.reduce((a, d) => a + d.count, 0);
                  return <div className="space-y-3">{analytics.devices.map((d) => { const meta = DEVICE_META[d.name] || { icon: Monitor, label: d.name, color: "text-muted-foreground" }; return <BarRow key={d.name} testid={`device-row-${d.name}`} label={meta.label} icon={meta.icon} iconColor={meta.color} count={d.count} total={total} />; })}</div>;
                })()}
              </div>

              <div className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-heading font-bold mb-4 flex items-center gap-2"><Globe size={18} className="text-rdcblue" /> Par pays</h3>
                {analytics.countries.length === 0 ? <p className="text-sm text-muted-foreground">Pas encore de données géographiques.</p> : (() => {
                  const total = analytics.countries.reduce((a, c) => a + c.count, 0);
                  return <div className="space-y-3">{analytics.countries.map((c) => <BarRow key={c.code || c.name} testid={`country-row-${c.code || "xx"}`} label={`${countryFlag(c.code)} ${c.name}`} count={c.count} total={total} />)}</div>;
                })()}
              </div>

              <div className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-heading font-bold mb-4 flex items-center gap-2"><MapPin size={18} className="text-rdcred" /> Par province (RDC)</h3>
                {analytics.provinces.length === 0 ? <p className="text-sm text-muted-foreground">Pas encore de données.</p> : (() => {
                  const total = analytics.provinces.reduce((a, p) => a + p.count, 0);
                  return <div className="space-y-3">{analytics.provinces.map((p) => <BarRow key={p.name} testid={`province-row`} label={p.name} count={p.count} total={total} />)}</div>;
                })()}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="wallet">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
            <StatCard icon={DollarSign} label="Total brut ($)" value={w.total_gross} color="bg-rdcblue/10 text-rdcblue" />
            <StatCard icon={TrendingUp} label="Commission ($)" value={w.total_commission} color="bg-rdcred/10 text-rdcred" />
            <StatCard icon={Wallet} label="Revenus nets ($)" value={w.total_net} color="bg-emerald-500/10 text-emerald-600" />
            <StatCard icon={Wallet} label="Solde dispo ($)" value={w.available} color="bg-emerald-500/10 text-emerald-600" />
            <StatCard icon={Loader2} label="En attente ($)" value={w.pending} color="bg-rdcgold/10 text-rdcgold" />
            <StatCard icon={ArrowDownToLine} label="Retiré ($)" value={w.withdrawn} color="bg-slate-500/10 text-slate-500" />
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 max-w-xl mb-5">
            <h3 className="font-heading font-bold mb-3 flex items-center gap-2"><ArrowDownToLine size={18} /> Demander un retrait</h3>
            <div className="flex flex-wrap gap-2">
              <Input type="number" placeholder="Montant ($)" value={wd.amount} onChange={(e) => setWd((p) => ({ ...p, amount: e.target.value }))} className="w-32" data-testid="wd-amount" />
              <Select value={wd.method} onValueChange={(v) => setWd((p) => ({ ...p, method: v }))}>
                <SelectTrigger className="w-40" data-testid="wd-method"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="mpesa">M-Pesa</SelectItem><SelectItem value="airtel">Airtel Money</SelectItem><SelectItem value="orange">Orange Money</SelectItem><SelectItem value="bank">Virement bancaire</SelectItem></SelectContent>
              </Select>
              <Input placeholder="Destination (numéro/compte)" value={wd.destination} onChange={(e) => setWd((p) => ({ ...p, destination: e.target.value }))} className="flex-1 min-w-40" data-testid="wd-destination" />
              <Button onClick={requestWithdrawal} data-testid="wd-submit">Demander</Button>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 mb-5" data-testid="supports-received">
            <h3 className="font-heading font-bold mb-3 flex items-center gap-2"><Heart size={18} className="text-rdcred" /> Soutiens reçus</h3>
            {supports.length === 0 ? <p className="text-sm text-muted-foreground py-3">Aucun soutien reçu pour l'instant.</p> :
              <div className="max-h-80 overflow-auto">
                {supports.map((s) => (
                  <div key={s.reference} className="flex items-center justify-between py-2 border-b border-border last:border-0 text-sm gap-3" data-testid={`support-row-${s.reference}`}>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{s.user_name || "Anonyme"}</p>
                      <p className="text-[11px] text-muted-foreground">{new Date(s.confirmed_at || s.created_at).toLocaleDateString("fr-FR")} · {s.message ? `« ${s.message} »` : "Soutien"}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-bold text-emerald-600">+${(s.net ?? s.amount).toLocaleString()}</p>
                      <p className="text-[10px] text-muted-foreground">brut ${s.amount?.toLocaleString?.() ?? s.amount}</p>
                    </div>
                  </div>
                ))}
              </div>}
          </div>

          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="font-heading font-bold mb-3">Historique des retraits</h3>
            {(walletData?.withdrawals || []).length === 0 ? <p className="text-sm text-muted-foreground py-3">Aucune demande.</p> :
              walletData.withdrawals.map((x) => (
                <div key={x.id} className="flex items-center justify-between py-2 border-b border-border last:border-0 text-sm">
                  <span className="capitalize">{x.method} · {new Date(x.created_at).toLocaleDateString("fr-FR")}</span>
                  <span className="flex items-center gap-3"><b>${x.amount}</b><span className="rounded-md bg-secondary px-2 py-0.5 text-[11px] capitalize">{x.status}</span></span>
                </div>
              ))}
          </div>
        </TabsContent>

        <TabsContent value="schedule">
          <div className="rounded-2xl border border-border bg-card p-5 max-w-2xl mb-5">
            <h3 className="font-heading font-bold mb-3 flex items-center gap-2"><Plus size={18} /> Ajouter une émission</h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
              <div className="col-span-2 sm:col-span-1">
                <label className="text-[11px] text-muted-foreground">Jour</label>
                <Select value={prog.day_of_week} onValueChange={(v) => setProg((p) => ({ ...p, day_of_week: v }))}>
                  <SelectTrigger data-testid="prog-day" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-[11px] text-muted-foreground">Début</label><Input type="time" value={prog.start_time} onChange={(e) => setProg((p) => ({ ...p, start_time: e.target.value }))} className="h-9" data-testid="prog-start" /></div>
              <div><label className="text-[11px] text-muted-foreground">Fin</label><Input type="time" value={prog.end_time} onChange={(e) => setProg((p) => ({ ...p, end_time: e.target.value }))} className="h-9" data-testid="prog-end" /></div>
              <div className="col-span-2 sm:col-span-1"><label className="text-[11px] text-muted-foreground">Titre</label><Input value={prog.title} onChange={(e) => setProg((p) => ({ ...p, title: e.target.value }))} placeholder="Journal…" className="h-9" data-testid="prog-title" /></div>
              <div className="col-span-2 sm:col-span-1"><label className="text-[11px] text-muted-foreground">Animateur</label><Input value={prog.host} onChange={(e) => setProg((p) => ({ ...p, host: e.target.value }))} placeholder="Optionnel" className="h-9" data-testid="prog-host" /></div>
            </div>
            <Button onClick={addProg} className="mt-3 font-semibold" data-testid="prog-add"><Plus size={15} className="mr-1" /> Ajouter</Button>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {DAYS.map((d, i) => {
              const rows = schedule.filter((s) => s.day_of_week === i);
              if (rows.length === 0) return null;
              return (
                <div key={i} className="rounded-2xl border border-border bg-card p-4">
                  <h4 className="font-heading font-bold text-sm mb-2 flex items-center gap-1.5"><CalendarDays size={14} className="text-primary" /> {d}</h4>
                  {rows.map((s) => (
                    <div key={s.id} className="flex items-center gap-2 py-1.5 border-b border-border last:border-0 text-sm" data-testid={`mgr-prog-${s.id}`}>
                      <span className="font-mono text-[11px] text-muted-foreground w-24 shrink-0">{s.start_time}-{s.end_time}</span>
                      <span className="flex-1 min-w-0 truncate">{s.title}</span>
                      <button onClick={() => delProg(s.id)} className="text-rdcred hover:opacity-70" data-testid={`mgr-prog-del-${s.id}`}><Trash2 size={13} /></button>
                    </div>
                  ))}
                </div>
              );
            })}
            {schedule.length === 0 && <p className="text-muted-foreground text-sm py-6">Aucune émission programmée. Ajoutez-en une ci-dessus.</p>}
          </div>
        </TabsContent>

        <TabsContent value="community">
          {/* Alerte aux abonnés */}
          <div className="rounded-2xl border border-border bg-card p-5 mb-6 max-w-2xl" data-testid="mgr-broadcast-card">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-9 h-9 rounded-xl bg-rdcred/10 text-rdcred flex items-center justify-center"><Megaphone size={18} /></span>
              <div>
                <h3 className="font-heading font-bold">Alerte aux abonnés</h3>
                <p className="text-xs text-muted-foreground">Envoyez une notification push à tous vos abonnés (ex. « Nous passons en direct ! »).</p>
              </div>
            </div>
            <div className="space-y-2 mt-3">
              <Input placeholder="Titre (optionnel)" value={broadcast.title} onChange={(e) => setBroadcast((p) => ({ ...p, title: e.target.value }))} data-testid="mgr-broadcast-title" maxLength={80} />
              <Textarea placeholder="Nous passons en direct ! Rejoignez-nous maintenant." value={broadcast.message} onChange={(e) => setBroadcast((p) => ({ ...p, message: e.target.value }))} data-testid="mgr-broadcast-message" maxLength={300} />
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-[11px] text-muted-foreground">Envoi limité à une alerte toutes les quelques minutes pour éviter le spam.</p>
                <Button onClick={sendBroadcast} disabled={broadcasting} className="font-semibold bg-rdcred hover:bg-rdcred/90 text-white" data-testid="mgr-broadcast-send">
                  {broadcasting ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Send size={15} className="mr-1.5" />} Envoyer l'alerte
                </Button>
              </div>
            </div>
          </div>

          {/* Modération des commentaires */}
          <div className="rounded-2xl border border-border bg-card p-5 max-w-2xl" data-testid="mgr-comments-card">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><MessagesSquare size={18} /></span>
              <div>
                <h3 className="font-heading font-bold">Commentaires</h3>
                <p className="text-xs text-muted-foreground">Répondez, épinglez ou masquez les commentaires de votre chaîne.</p>
              </div>
            </div>
            {(() => {
              const top = mgrComments.filter((c) => !c.parent_id);
              const byParent = mgrComments.reduce((a, c) => { if (c.parent_id) { (a[c.parent_id] = a[c.parent_id] || []).push(c); } return a; }, {});
              if (top.length === 0) return <p className="text-sm text-muted-foreground py-4">Aucun commentaire pour l'instant.</p>;
              return (
                <div className="space-y-3">
                  {top.map((c) => {
                    const hidden = c.status === "hidden";
                    return (
                      <div key={c.id} className={`rounded-xl border p-3 ${hidden ? "border-dashed border-border bg-secondary/40 opacity-70" : "border-border"}`} data-testid={`mgr-comment-${c.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold shrink-0">{c.user_name?.[0]}</span>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold truncate flex items-center gap-1.5">
                                {c.user_name}
                                {c.pinned && <span className="inline-flex items-center gap-0.5 rounded-full bg-rdcgold/15 text-amber-600 px-1.5 py-0.5 text-[10px] font-bold"><Pin size={10} /> Épinglé</span>}
                                {hidden && <span className="inline-flex items-center gap-0.5 rounded-full bg-secondary text-muted-foreground px-1.5 py-0.5 text-[10px] font-bold"><EyeOff size={10} /> Masqué</span>}
                              </p>
                              <p className="text-[11px] text-muted-foreground">{new Date(c.created_at).toLocaleDateString("fr-FR")}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => pinComment(c)} disabled={cmtBusy === c.id} title={c.pinned ? "Retirer l'épingle" : "Épingler"} data-testid={`mgr-comment-pin-${c.id}`}
                              className={`w-7 h-7 rounded-lg flex items-center justify-center hover:bg-secondary ${c.pinned ? "text-amber-600" : "text-muted-foreground"}`}>
                              {c.pinned ? <PinOff size={14} /> : <Pin size={14} />}
                            </button>
                            <button onClick={() => hideComment(c)} disabled={cmtBusy === c.id} title={hidden ? "Réafficher" : "Masquer"} data-testid={`mgr-comment-hide-${c.id}`}
                              className={`w-7 h-7 rounded-lg flex items-center justify-center hover:bg-secondary ${hidden ? "text-emerald-600" : "text-muted-foreground"}`}>
                              {hidden ? <Eye size={14} /> : <EyeOff size={14} />}
                            </button>
                            <button onClick={() => { setReplyFor(replyFor === c.id ? null : c.id); setReplyText(""); }} disabled={cmtBusy === c.id} title="Répondre" data-testid={`mgr-comment-reply-btn-${c.id}`}
                              className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-secondary text-primary">
                              <Reply size={14} />
                            </button>
                          </div>
                        </div>
                        <p className="text-sm mt-2 leading-relaxed">{c.content}</p>

                        {(byParent[c.id] || []).map((r) => (
                          <div key={r.id} className="mt-2 ml-5 pl-3 border-l-2 border-border" data-testid={`mgr-comment-reply-${r.id}`}>
                            <p className="text-xs font-semibold flex items-center gap-1.5">
                              {r.user_name}
                              {r.is_manager_reply && <span className="inline-flex items-center gap-0.5 rounded-full bg-rdcblue/10 text-rdcblue px-1.5 py-0.5 text-[10px] font-bold"><BadgeCheck size={10} /> Réponse officielle</span>}
                            </p>
                            <p className="text-xs mt-0.5 leading-relaxed">{r.content}</p>
                          </div>
                        ))}

                        {replyFor === c.id && (
                          <div className="mt-3 flex gap-2" data-testid={`mgr-comment-reply-box-${c.id}`}>
                            <input autoFocus value={replyText} onChange={(e) => setReplyText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && replyComment(c)}
                              placeholder="Votre réponse officielle…" data-testid={`mgr-comment-reply-input-${c.id}`}
                              className="flex-1 rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
                            <Button onClick={() => replyComment(c)} disabled={cmtBusy === c.id || !replyText.trim()} size="sm" data-testid={`mgr-comment-reply-send-${c.id}`}>
                              {cmtBusy === c.id ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </TabsContent>

        <TabsContent value="profile">          <div className="rounded-2xl border border-border bg-card p-5 space-y-3 max-w-xl">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Logo</label>
                <div className="flex items-center gap-3">
                  <span className="w-16 h-16 rounded-xl border border-border bg-white flex items-center justify-center overflow-hidden shrink-0">
                    {dash.media.logo ? <img src={dash.media.logo} alt="logo" className="w-full h-full object-cover" /> : <ImageIcon size={20} className="text-muted-foreground" />}
                  </span>
                  <label className="cursor-pointer inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-2 text-sm font-medium hover:border-primary/50" data-testid="mgr-upload-logo">
                    {uploading === "logo" ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Changer
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) setCropState({ file, kind: "logo" }); e.target.value = ""; }} />
                  </label>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Bannière</label>
                <div className="flex items-center gap-3">
                  <span className="w-24 h-16 rounded-xl border border-border bg-white flex items-center justify-center overflow-hidden shrink-0">
                    {dash.media.banner ? <img src={dash.media.banner} alt="bannière" className="w-full h-full object-cover" /> : <ImageIcon size={20} className="text-muted-foreground" />}
                  </span>
                  <label className="cursor-pointer inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-2 text-sm font-medium hover:border-primary/50" data-testid="mgr-upload-banner">
                    {uploading === "banner" ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Changer
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) setCropState({ file, kind: "banner" }); e.target.value = ""; }} />
                  </label>
                </div>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">Formats acceptés : JPG, PNG, WEBP, GIF (max 5 Mo).</p>
            <div className="h-px bg-border my-1" />
            <Textarea placeholder="Description" value={profile.description} onChange={(e) => setProfile((p) => ({ ...p, description: e.target.value }))} data-testid="mgr-desc" />
            <Input placeholder="Site web" value={profile.website} onChange={(e) => setProfile((p) => ({ ...p, website: e.target.value }))} data-testid="mgr-website" />
            <Input placeholder="Adresse" value={profile.address} onChange={(e) => setProfile((p) => ({ ...p, address: e.target.value }))} data-testid="mgr-address" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Téléphone contact" value={profile.contacts_phone} onChange={(e) => setProfile((p) => ({ ...p, contacts_phone: e.target.value }))} data-testid="mgr-phone" />
              <Input placeholder="Email contact" value={profile.contacts_email} onChange={(e) => setProfile((p) => ({ ...p, contacts_email: e.target.value }))} data-testid="mgr-email" />
            </div>
            <p className="text-[11px] text-muted-foreground">Le nom officiel et le statut de vérification sont gérés par l'administration.</p>

            <div className="h-px bg-border my-1" />
            <div>
              <p className="text-sm font-medium flex items-center gap-1.5"><Link2 size={15} className="text-primary" /> Réseaux sociaux</p>
              <p className="text-[11px] text-muted-foreground mb-2.5">Ajoutez les liens de vos réseaux. Ils s'afficheront sur la page publique de votre chaîne.</p>
              <div className="grid sm:grid-cols-2 gap-2.5">
                {SOCIALS.map(({ key, label, icon: SIcon, color, ph }) => (
                  <div key={key} className="flex items-center gap-2 rounded-xl border border-border px-2.5 py-1.5">
                    <SIcon size={17} className={`${color} shrink-0`} />
                    <input
                      value={(profile.socials || {})[key] || ""}
                      onChange={(e) => setProfile((p) => ({ ...p, socials: { ...(p.socials || {}), [key]: e.target.value } }))}
                      placeholder={ph}
                      aria-label={label}
                      data-testid={`mgr-social-${key}`}
                      className="flex-1 min-w-0 bg-transparent outline-none text-sm placeholder:text-muted-foreground/60"
                    />
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={saveProfile} className="font-semibold" data-testid="mgr-save-profile">Enregistrer</Button>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 mt-5 max-w-xl flex items-center gap-3" data-testid="mgr-weekly-report-card">
            <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${weeklyReport ? "bg-rdcblue/10 text-rdcblue" : "bg-secondary text-muted-foreground"}`}><BarChart3 size={20} /></span>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-sm">Rapport d'audience hebdomadaire</p>
              <p className="text-xs text-muted-foreground">Recevez chaque lundi par email un récap (temps consommé, top pays/appareils, vues, likes) de vos médias.</p>
            </div>
            <button onClick={toggleWeekly} data-testid="mgr-weekly-report-toggle" role="switch" aria-checked={weeklyReport}
              className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${weeklyReport ? "bg-emerald-500" : "bg-border"}`}>
              <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${weeklyReport ? "translate-x-5" : ""}`} />
            </button>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 mt-5 max-w-xl" data-testid="mgr-certification-card">
            <div className="flex items-center gap-2 mb-1">
              <BadgeCheck size={20} className="text-rdcgold" />
              <h3 className="font-heading font-bold">Certification de la chaîne</h3>
            </div>
            {cert?.certified ? (
              <div className="mt-2 space-y-2" data-testid="mgr-cert-certified">
                <div className="flex items-center gap-2 rounded-xl bg-rdcgold/10 p-3">
                  <BadgeCheck size={18} className="text-rdcgold" /><p className="text-sm font-semibold">Votre chaîne est certifiée 🏅</p>
                </div>
                <a href={`${API}/media/${selected}/certificate`} target="_blank" rel="noreferrer" data-testid="mgr-download-certificate"
                  className="inline-flex items-center gap-1.5 rounded-full bg-rdcblue text-white px-4 py-2 text-sm font-semibold hover:bg-rdcblue/90">
                  <FileText size={15} /> Télécharger le certificat (PDF)
                </a>
              </div>
            ) : cert?.certification?.status === "pending" ? (
              <p className="text-sm text-amber-600 bg-amber-500/10 rounded-xl p-3 mt-2" data-testid="mgr-cert-pending">Votre demande de certification est en cours d'examen par l'administration.</p>
            ) : (cert?.status !== "approved" && cert?.status !== "verified") ? (
              <p className="text-sm text-muted-foreground mt-2" data-testid="mgr-cert-locked">Votre chaîne doit d'abord être validée par l'administration avant de pouvoir demander la certification.</p>
            ) : (
              <>
                {cert?.certification?.status === "rejected" && <p className="text-sm text-rdcred bg-rdcred/10 rounded-xl p-3 mt-2 mb-1">Demande précédente refusée. {cert.certification.admin_note}</p>}
                <p className="text-xs text-muted-foreground mb-3">Fournissez les documents suivants pour obtenir le badge « Chaîne certifiée ».</p>
                <div className="space-y-2">
                  {[["enterprise", "Document d'enregistrement de l'entreprise"], ["authorization", "Autorisation de diffusion"], ["id_card", "Carte d'identité du responsable"]].map(([k, label]) => (
                    <div key={k} className="rounded-lg border border-border p-2.5">
                      <div className="flex items-center gap-2">
                        <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${certDocs[k] ? "bg-emerald-500/15 text-emerald-600" : "bg-secondary text-muted-foreground"}`}>
                          {certDocs[k] ? <CheckCircle2 size={16} /> : <FileText size={16} />}
                        </span>
                        <span className="text-sm flex-1">{label}</span>
                        <label className="cursor-pointer inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:border-primary/50" data-testid={`mgr-cert-upload-${k}`}>
                          {certUploading === k ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {certDocs[k] ? "Remplacer" : "Téléverser"}
                          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) uploadCertDoc(k, file); e.target.value = ""; }} />
                        </label>
                      </div>
                      <div className="flex items-center gap-2 mt-2 pl-10">
                        <span className="text-[11px] text-muted-foreground">Date d'expiration (optionnel)</span>
                        <input type="date" value={certExp[k]} onChange={(e) => setCertExp((p) => ({ ...p, [k]: e.target.value }))} data-testid={`mgr-cert-expiry-${k}`}
                          className="text-xs rounded-md border border-border bg-card px-2 py-1 outline-none focus:border-primary" />
                      </div>
                    </div>
                  ))}
                </div>
                <Button onClick={requestCert} disabled={certRequesting} className="mt-3 font-semibold bg-rdcgold text-black hover:bg-rdcgold/90" data-testid="mgr-cert-request">
                  {certRequesting ? <Loader2 size={15} className="animate-spin mr-1" /> : <BadgeCheck size={15} className="mr-1" />} Demander la certification
                </Button>
                <p className="text-[11px] text-muted-foreground mt-2">Formats acceptés : PDF ou image (max 10 Mo par document).</p>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {cropState && (
        <CropModal
          file={cropState.file}
          kind={cropState.kind}
          onCancel={() => setCropState(null)}
          onDone={async (blobFile) => { const k = cropState.kind; setCropState(null); await uploadAsset(k, blobFile); }}
        />
      )}
    </div>
  );
}
